import Portal from 'Components/Portal';
import { useLayoutEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { useCurrentPopup } from '../useCurrentPopup';
import { PopupRoot } from './PopupRoot';
import { PopupPlacementType, PopupProps } from './type';

const createPopperModifiers = (
  placement: PopupPlacementType = 'bottom',
  offset: [number, number],
): any => ({
  modifiers: [
    {
      name: 'offset',
      options: {
        offset,
      },
    },
  ],
  placement:
    placement === 'screen-centered' ||
    placement === 'screen-offset-top' ||
    placement === 'stick-to-bottom'
      ? 'auto'
      : placement,
});

export default function Popup<CTX>(props: PopupProps<CTX>) {
  const {
    id: popupId,
    placement,
    children,
    matchAnchorWidth,
    minWidth,
    offset = [0, 4],
    overlay = false,
    onMouseEnter,
    onMouseLeave,
  } = props;

  const { isOpened, anchor, popupRoot, contextData, setPopupRoot } =
    useCurrentPopup();
  const [anchorWindowSize, setAnchorWindowSize] = useState<number>();
  const popper = usePopper(
    anchor,
    popupRoot,
    createPopperModifiers(placement, offset),
  );

  const updatePopper = async () => {
    popper.update && (await popper.update());
  };

  useLayoutEffect(() => {
    if (!anchor) return;
    if (matchAnchorWidth) {
      if (minWidth) {
        if (anchor.clientWidth > minWidth) {
          setAnchorWindowSize(anchor.clientWidth);
        } else {
          setAnchorWindowSize(minWidth);
        }
      } else {
        setAnchorWindowSize(anchor.clientWidth);
      }
    }

    updatePopper();
  }, [
    anchor,
    popper.update,
    matchAnchorWidth,
    anchor?.clientHeight,
    anchor?.clientWidth,
  ]);
  if (!isOpened) return <></>;

  return (
    <>
      <Portal>
        {overlay && (
          <div className="z-50 w-screen h-screen fixed inset-0  bg-black opacity-25"></div>
        )}
        <PopupRoot
          popupId={popupId || ''}
          attributes={popper.attributes}
          styles={popper.styles}
          placement={placement}
          setPopoverRef={setPopupRoot}
          onMouseEnter={onMouseEnter}
          width={`${anchorWindowSize}px`}
          onMouseLeave={onMouseLeave}
        >
          {children && typeof children === 'function'
            ? children({ data: contextData, anchor })
            : children}
        </PopupRoot>
      </Portal>
    </>
  );
}
